import React, { KeyboardEvent, useEffect, useRef, useState } from "react"
import { ButtonWrapper, Comment, CommentInput, Info } from "./Operations.styled"
import SecondaryButton from "../../../Ui/Buttons/SecondaryButton"
import { ReactComponent as GearIcon } from "images/icons/gear.svg"
import { ReactComponent as TrashIcon } from "images/icons/trash-can.svg"
import { ReactComponent as XmarkIcon } from "images/icons/xmark.svg"
import { ReactComponent as CheckIcon } from "images/icons/check.svg"

import Popup from "../../../Ui/Popup"
import { useMutation } from "@apollo/client"
import DELETE_ORDER_COMMENT from "../../../../graphql/mutations/order/comment/DeleteOrderComment"
import UPDATE_ORDER_COMMENT from "../../../../graphql/mutations/order/comment/UpdateOrderComment"
import { useSelector } from "react-redux"
import { selectUserAttributes } from "lib/store/services/auth/selectors"

type Props = {
  comment: string | undefined
  commentId: string
  orderId: string
  refetch: () => void
  actorEmail: string
}

const CommentInfo = ({ comment, commentId, orderId, refetch, actorEmail }: Props) => {
  const [showPopup, setShowPopup] = useState(false)
  const [editComment, setEditComment] = useState(false)
  const [updatedComment, setUpdatedComment] = useState(comment)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const userAttributes = useSelector(selectUserAttributes)

  const editPermission = () => userAttributes.email === actorEmail

  const [deleteComment, { loading }] = useMutation(DELETE_ORDER_COMMENT, {
    variables: { commentId: commentId, orderId: orderId },
    onCompleted: () => {
      refetch()
      setShowPopup(false)
    }
  })

  const [updateComment, { loading: updateLoading }] = useMutation(UPDATE_ORDER_COMMENT, {
    variables: { commentId: commentId, orderId: orderId, comment: { text: updatedComment } },
    onCompleted: () => {
      refetch()
      setEditComment(false)
    }
  })

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      updateComment()
    }
  }

  const handleTextareaChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setUpdatedComment(e.currentTarget.value)
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  useEffect(() => {
    if (editComment && textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [editComment])

  const renderCommentWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g

    return text.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        const url = part.startsWith("www") ? `https://${part}` : part
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        )
      }
      return <span key={index}>{part}</span>
    })
  }

  return (
    <Info className={updateLoading ? "blur" : ""}>
      {showPopup && (
        <Popup
          title="Delete comment"
          subtitle={`Are you sure you want to delete the following comment?`}
          handleCloseClick={() => setShowPopup(false)}
          handleOkClick={() => deleteComment()}
          type="delete"
          loading={loading}
        >
          <Comment>
            -{comment}
          </Comment>
        </Popup>
      )}
      {editComment ? (
        <CommentInput
          ref={textareaRef}
          name={"comment"}
          value={updatedComment}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          maxLength={300}
        />
      ) : (
        <Comment>
          {"- "}
          {comment ? renderCommentWithLinks(comment) : ""}
        </Comment>
      )}
      {editPermission() && (
        <ButtonWrapper>
          <SecondaryButton handleClick={() => setEditComment(!editComment)}>
            {editComment ? <XmarkIcon /> : <GearIcon />}
          </SecondaryButton>
          {editComment && (
            <SecondaryButton handleClick={() => updateComment()}>
              <CheckIcon />
            </SecondaryButton>
          )}
          {!editComment && (
            <SecondaryButton handleClick={() => setShowPopup(true)}>
              <TrashIcon />
            </SecondaryButton>
          )}
        </ButtonWrapper>
      )}
    </Info>
  )
}

export default CommentInfo
